/**
 * La extensión `.js` es obligatoria aquí y no en el resto de la web: este
 * archivo lo compila también la configuración del servidor —lo arrastran las
 * pruebas de `tests/web/`—, que resuelve al estilo de Node y la exige. Es un
 * import de tipo, así que desaparece al compilar y no llega al navegador.
 */
import type { PuntajeDeEsquema } from './api.js';

/**
 * A qué color va cada dimensión del mapa de esquemas — HU-09.
 *
 * ESTÁ SEPARADO DE `graficos.tsx` A PROPÓSITO: aquí no hay JSX, así que esto se
 * puede probar desde `tests/`, que compila con la configuración del servidor y
 * no sabe de React. Sin esta separación, la única regla con criterio de todo el
 * dibujo se quedaría sin pruebas.
 *
 * EL COLOR DICE DE QUÉ DIMENSIÓN ES, NO CUÁNTO PESA. Es la decisión de la que
 * cuelga el diseño entero de la pantalla. Un semáforo verde-amarillo-rojo sería
 * más vistoso y convertiría el mapa en un boletín de notas: «Imperfección /
 * Vergüenza» en rojo es una frase muy dura para leerla solo, en el móvil, un
 * martes. Lo que sobresale sobresale por tamaño; el color solo agrupa.
 */

/** Cuántos colores hay en la paleta. Están en `estilos.css` como `--dim-1`… */
const COLORES = 5;

/**
 * Reparte los colores entre las dimensiones que aparezcan.
 *
 * POR ORDEN DE APARICIÓN Y NO POR UN MAPA FIJO DE `D1` A `D5`. Las claves de
 * dimensión son parámetro clínico: hoy son cinco y se llaman así porque así las
 * escribió Ángela, y puede renombrarlas o añadir una sexta cualquier día. Un
 * mapa fijo se caería al gris de reserva en silencio, y justo en la pantalla que
 * la persona más va a mirar.
 *
 * Como los puntajes llegan de mayor a menor, el color 1 acaba siendo el de la
 * dimensión que más pesa. No es indispensable, pero ayuda: el primer círculo
 * lleva siempre el color 1.
 */
export function coloresPorDimension(puntajes: PuntajeDeEsquema[]): Map<string, number> {
  const orden = new Map<string, number>();
  for (const p of puntajes) {
    if (p.dimension === null || orden.has(p.dimension)) continue;
    orden.set(p.dimension, (orden.size % COLORES) + 1);
  }
  return orden;
}

/**
 * La clase de color de un esquema.
 *
 * NO LLEVA PREFIJO POR COMPONENTE —no hay `petalo--1` y `barra--1`— porque lo
 * único que hace la clase es fijar la variable `--dim`. Cada pieza la usa a su
 * manera desde la hoja de estilos: el pétalo como relleno, la tarjeta como
 * cinta. Así los colores se declaran una sola vez y nada puede desincronizarse.
 *
 * `color--sin` es el gris de reserva, para un esquema sin dimensión.
 */
export function claseDeColor(esquema: PuntajeDeEsquema, colores: Map<string, number>): string {
  const numero = esquema.dimension === null ? undefined : colores.get(esquema.dimension);
  return numero === undefined ? 'color--sin' : `color--${String(numero)}`;
}

/** Cuántos esquemas van en los círculos de arriba del mapa. */
export const ESQUEMAS_PRINCIPALES = 4;

/**
 * Los esquemas de los círculos: los cuatro que más pesan.
 *
 * REEMPLAZÓ A LAS CINCO PUERTAS (un círculo por área) en octubre de 2026, a
 * pedido del cliente: los círculos son de esquemas, no de áreas ni de
 * necesidades, y solo los cuatro principales.
 *
 * BASTA CON CORTAR LA LISTA porque el servidor ya la manda de mayor a menor, y
 * a igualdad de puntaje por el orden del catálogo (`correccion.ts`): un empate
 * en el cuarto puesto se resuelve siempre igual, y quien vuelve a abrir su mapa
 * ve los mismos cuatro.
 *
 * UN ESQUEMA SIN DIMENSIÓN TAMBIÉN ENTRA: es un esquema como los demás, solo
 * que sin área, y sale con el gris de reserva.
 */
export function esquemasPrincipales(puntajes: PuntajeDeEsquema[]): PuntajeDeEsquema[] {
  return puntajes.slice(0, ESQUEMAS_PRINCIPALES);
}
